/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */
import * as React from 'react';

import { IMfrmCsrVoteModuleData } from './mfrm-csr-vote-module.data';
import { IMfrmCsrVoteModuleProps } from './mfrm-csr-vote-module.props.autogenerated';

import MsDyn365, { ContentEditableEvent, IImageData, IImageSettings,Image, RichTextComponent } from '@msdyn365-commerce/core';
import { Button } from '@msdyn365-commerce-modules/utilities';

export interface IMfrmCsrVoteModuleViewProps extends IMfrmCsrVoteModuleProps<IMfrmCsrVoteModuleData> {
    csrHeaderImage?: IImageData;
    csrHeader?: React.ReactNode;
    csrContent?: React.ReactNode;
    csrCTAText?: React.ReactNode;
    ctaButton?: React.ReactNode;
}

/**
 *
 * MfrmCsrVoteModule component
 * @extends {React.PureComponent<IMfrmCsrVoteModuleProps<IMfrmCsrVoteModuleData>>}
 */
class MfrmCsrVoteModule extends React.PureComponent<IMfrmCsrVoteModuleProps<IMfrmCsrVoteModuleData>> {
    public render(): JSX.Element | null {
        const { csrHeader, csrContent, csrCTAText } = this.props.config;
        const ctaLinkUrl = this.props.config.csrCTALink?.linkUrl.destinationUrl;

        const handleClick = () => {
            if (MsDyn365.isBrowser) {
                window.open(ctaLinkUrl);
            }
        };

        const renderHeaderText = csrHeader && (
            <RichTextComponent
                text={csrHeader}
                className='d5-mfrm-csr-vote-module__header-text'
                editProps={{ onEdit: this.handleTextChange, requestContext: this.props.context.request }}
            />
        );

        const renderContentText = csrContent && (
            <RichTextComponent
                text={csrContent}
                className='d5-mfrm-csr-vote-module__content-text'
                editProps={{ onEdit: this.handleTextChange, requestContext: this.props.context.request }}
            />
        );

        const renderButton = csrCTAText && (
            <div className='d5-mfrm-csr-vote-module__btn-container'>
                <Button
                    className='d5-mfrm-csr-vote-module__blue-cta '
                    onClick={handleClick}
                >
                    {csrCTAText}
                </Button>
            </div>
        );

        const CSRVoteViewProps = {
            ...this.props,
            csrHeaderImage: this._getVoteImage(),
            csrHeader: renderHeaderText,
            csrContent: renderContentText,
            ctaButton: renderButton
        };


        return (this.props.renderView(CSRVoteViewProps));
    }

    public handleTextChange = (event: ContentEditableEvent) => this.props.config.csrContent = event.target.value;

    private _getVoteImage(): React.ReactNode {
        const { csrHeaderImage } = this.props.config;

        const defaultImageSettings: IImageSettings = {
            viewports: {
                xs: { q: 'w=168&h=71&m=8', w: 0, h: 0 },
                sm: { q: 'w=168&h=71&m=8', w: 0, h: 0 },
                md: { q: 'w=168&h=71&m=8', w: 0, h: 0 },
                lg: { q: 'w=168&h=71&m=8', w: 0, h: 0 }
            },
            lazyload: true
        };

        return (
            <Image
                {...csrHeaderImage}
                alt={csrHeaderImage?.altText}
                src={csrHeaderImage?.src || ''}
                className='d5-mfrm-csr-vote-module__img'
                gridSettings={this.props.context.request.gridSettings!}
                imageSettings={csrHeaderImage && csrHeaderImage.imageSettings || defaultImageSettings}
            />
        );
    }

}

export default MfrmCsrVoteModule;